"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { appendSkippedDropId, getDisplayQuickVoteAmounts } from "@/hooks/memesQuickVote.helpers";
import { useMemesQuickVoteActiveDrop } from "@/hooks/useMemesQuickVoteActiveDrop";
import { useMemesQuickVoteContext } from "@/hooks/useMemesQuickVoteContext";
import { useMemesQuickVoteDiscovery } from "@/hooks/useMemesQuickVoteDiscovery";
import { useMemesQuickVoteStorage } from "@/hooks/useMemesQuickVoteStorage";
import { useMemesQuickVoteSubmit } from "@/hooks/useMemesQuickVoteSubmit";
import { useMemesQuickVoteSummary } from "@/hooks/useMemesQuickVoteSummary";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import { WAVE_VOTING_LABELS } from "@/helpers/waves/waves.constants";
import { useCallback, useContext, useMemo, useState } from "react";
type UseMemesQuickVoteQueueOptions = {
  readonly enabled?: boolean | undefined;
  readonly sessionId: number;
};
type UseMemesQuickVoteQueueResult = {
  readonly activeDrop: ExtendedDrop | null;
  readonly hasDiscoveryError: boolean;
  readonly isExhausted: boolean;
  readonly isLoading: boolean;
  readonly isReady: boolean;
  readonly isVoting: boolean;
  readonly latestUsedAmount: number | null;
  readonly queue: ExtendedDrop[];
  readonly recentAmounts: number[];
  readonly remainingCount: number;
  readonly retryDiscovery: () => void;
  readonly submitVote: (drop: ExtendedDrop, amount: number | string) => Promise<boolean>;
  readonly skipDrop: (drop: ExtendedDrop) => void;
  readonly uncastPower: number | null;
  readonly votingLabel: string | null;
};
type OptimisticRemainingPowerState = {
  readonly key: string;
  readonly pendingVotes: {
    readonly amount: number;
    readonly dropId: string;
  }[];
  readonly optimisticVoteCount: number;
  readonly settledRemainingPower: number | null;
  readonly summaryBaselineCount: number | null;
};
type UseMemesQuickVoteQueueDerivedState = Pick<UseMemesQuickVoteQueueResult, "activeDrop" | "hasDiscoveryError" | "isExhausted" | "isLoading" | "isReady" | "latestUsedAmount" | "queue" | "recentAmounts" | "remainingCount" | "uncastPower" | "votingLabel">;
type UseDerivedMemesQuickVoteQueueStateOptions = {
  readonly activeDropCandidate: ExtendedDrop | null;
  readonly activeIsLoading: boolean;
  readonly discoveredQueue: readonly ApiDrop[];
  readonly effectiveOptimisticRemainingPower: number | null;
  readonly enabled: boolean;
  readonly hasPageFetchError: boolean;
  readonly isDiscoveryExhausted: boolean;
  readonly isFetchingPage: boolean;
  readonly isSettingsLoaded: boolean;
  readonly isSummaryPending: boolean;
  readonly isSummaryQuickVoteEnabled: boolean;
  readonly isSummarySuccess: boolean;
  readonly recentAmountsByRecency: readonly number[];
  readonly serverCount: number | null;
  readonly summaryCount: number;
  readonly unreflectedVoteCount: number;
  readonly summaryVotingLabel: string | null;
};
const getOptimisticRemainingPowerKey = ({
  contextProfile,
  memesWaveId,
  sessionId
}: {
  readonly contextProfile: string | null | undefined;
  readonly memesWaveId: string | null | undefined;
  readonly sessionId: number;
}): string => `${sessionId}:${memesWaveId ?? ""}:${contextProfile ?? ""}`;
const createInitialOptimisticRemainingPowerState = (key: string): OptimisticRemainingPowerState => ({
  key,
  pendingVotes: [],
  optimisticVoteCount: 0,
  settledRemainingPower: null,
  summaryBaselineCount: null
});
const getCurrentOptimisticRemainingPowerState = ({
  key,
  state
}: {
  readonly key: string;
  readonly state: OptimisticRemainingPowerState;
}): OptimisticRemainingPowerState => state.key === key ? state : createInitialOptimisticRemainingPowerState(key);
const deriveSummaryOptimismState = ({
  hasPendingVotes,
  isSummarySuccess,
  optimisticVoteCount,
  summaryBaselineCount,
  summaryCount
}: {
  readonly hasPendingVotes: boolean;
  readonly isSummarySuccess: boolean;
  readonly optimisticVoteCount: number;
  readonly summaryBaselineCount: number | null;
  readonly summaryCount: number;
}) => {
  if (summaryBaselineCount === null || optimisticVoteCount === 0) {
    return {
      isSettled: true,
      unreflectedVoteCount: 0
    };
  }
  const reflectedVoteCount = isSummarySuccess ? Math.max(0, summaryBaselineCount - summaryCount) : 0;
  const isSettled = !hasPendingVotes && isSummarySuccess && reflectedVoteCount >= optimisticVoteCount;
  return {
    isSettled,
    unreflectedVoteCount: isSettled ? 0 : Math.max(0, optimisticVoteCount - reflectedVoteCount)
  };
};
const clampDropMaxRating = (drop: ExtendedDrop, optimisticRemainingPower: number | null): ExtendedDrop => {
  const profileContext = drop.context_profile_context;
  const maxRating = profileContext?.max_rating;
  if (optimisticRemainingPower === null || profileContext?.rating !== 0 || typeof maxRating !== "number") {
    return drop;
  }
  const nextMaxRating = Math.max(0, Math.min(maxRating, optimisticRemainingPower));
  if (nextMaxRating === maxRating) {
    return drop;
  }
  return {
    ...drop,
    context_profile_context: {
      ...profileContext,
      max_rating: nextMaxRating
    }
  };
};
const isVoteableQuickVoteDrop = (drop: ExtendedDrop | null): boolean => {
  const profileContext = drop?.context_profile_context;
  return profileContext?.rating === 0 && typeof profileContext.max_rating === "number" && profileContext.max_rating > 0;
};
const useDerivedMemesQuickVoteQueueState = t0 => {
  const $ = _c(29);
  const {
    activeDropCandidate,
    activeIsLoading,
    discoveredQueue,
    effectiveOptimisticRemainingPower,
    enabled,
    hasPageFetchError,
    isDiscoveryExhausted,
    isFetchingPage,
    isSettingsLoaded,
    isSummaryPending,
    isSummaryQuickVoteEnabled,
    isSummarySuccess,
    recentAmountsByRecency,
    serverCount,
    summaryCount,
    unreflectedVoteCount,
    summaryVotingLabel
  } = t0;
  let t1;
  if ($[0] !== activeDropCandidate || $[1] !== effectiveOptimisticRemainingPower) {
    t1 = activeDropCandidate ? clampDropMaxRating(activeDropCandidate, effectiveOptimisticRemainingPower) : null;
    $[0] = activeDropCandidate;
    $[1] = effectiveOptimisticRemainingPower;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const clampedActiveDrop = t1;
  let t2;
  if ($[3] !== clampedActiveDrop) {
    t2 = isVoteableQuickVoteDrop(clampedActiveDrop) ? clampedActiveDrop : null;
    $[3] = clampedActiveDrop;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const activeDrop = t2;
  let t3;
  if ($[5] !== clampedActiveDrop || $[6] !== discoveredQueue || $[7] !== effectiveOptimisticRemainingPower) {
    bb0: {
      let t4;
      if ($[9] !== effectiveOptimisticRemainingPower) {
        t4 = drop => clampDropMaxRating(drop, effectiveOptimisticRemainingPower);
        $[9] = effectiveOptimisticRemainingPower;
        $[10] = t4;
      } else {
        t4 = $[10];
      }
      const clampedQueue = discoveredQueue.map(convertApiDropToExtendedDrop).map(t4);
      if (!clampedActiveDrop) {
        t3 = clampedQueue;
        break bb0;
      }
      let t5;
      if ($[11] !== clampedActiveDrop) {
        t5 = drop_0 => drop_0.id === clampedActiveDrop.id ? clampedActiveDrop : drop_0;
        $[11] = clampedActiveDrop;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      t3 = clampedQueue.map(t5);
    }
    $[5] = clampedActiveDrop;
    $[6] = discoveredQueue;
    $[7] = effectiveOptimisticRemainingPower;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const queue = t3;
  let t4;
  if ($[13] !== recentAmountsByRecency) {
    t4 = getDisplayQuickVoteAmounts(recentAmountsByRecency);
    $[13] = recentAmountsByRecency;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const recentAmounts = t4;
  let t5;
  if ($[15] !== recentAmountsByRecency) {
    t5 = recentAmountsByRecency.at(-1) ?? null;
    $[15] = recentAmountsByRecency;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const latestUsedAmount = t5;
  const uncastPower = activeDrop?.context_profile_context?.max_rating ?? null;
  const votingLabel = activeDrop ? WAVE_VOTING_LABELS[activeDrop.wave.voting_credit_type] : summaryVotingLabel;
  const remainingCount = isSummarySuccess ? Math.max(queue.length, Math.max(0, summaryCount - unreflectedVoteCount)) : Math.max(serverCount ?? 0, queue.length);
  const hasDiscoveryError = enabled && hasPageFetchError;
  const isQuickVoteUnavailable = enabled && isSettingsLoaded && !isSummaryQuickVoteEnabled;
  const activeProfileContext = clampedActiveDrop?.context_profile_context;
  const isPowerExhausted = enabled && !hasDiscoveryError && activeProfileContext?.rating === 0 && typeof activeProfileContext.max_rating === "number" && activeProfileContext.max_rating <= 0;
  const isExhausted = enabled && (isQuickVoteUnavailable || isDiscoveryExhausted || isPowerExhausted);
  const isLoading = enabled && !hasDiscoveryError && !isExhausted && !activeDropCandidate && (!isSettingsLoaded || isSummaryQuickVoteEnabled && isSummaryPending || isFetchingPage || activeIsLoading);
  const t6 = activeDrop !== null;
  let t7;
  if ($[17] !== activeDrop || $[18] !== hasDiscoveryError || $[19] !== isExhausted || $[20] !== isLoading || $[21] !== latestUsedAmount || $[22] !== queue || $[23] !== recentAmounts || $[24] !== remainingCount || $[25] !== t6 || $[26] !== uncastPower || $[27] !== votingLabel) {
    t7 = {
      activeDrop,
      hasDiscoveryError,
      isExhausted,
      isLoading,
      isReady: t6,
      latestUsedAmount,
      queue,
      recentAmounts,
      remainingCount,
      uncastPower,
      votingLabel
    };
    $[17] = activeDrop;
    $[18] = hasDiscoveryError;
    $[19] = isExhausted;
    $[20] = isLoading;
    $[21] = latestUsedAmount;
    $[22] = queue;
    $[23] = recentAmounts;
    $[24] = remainingCount;
    $[25] = t6;
    $[26] = uncastPower;
    $[27] = votingLabel;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  return t7;
};
export const useMemesQuickVoteQueue = t0 => {
  const $ = _c(81);
  const {
    enabled: t1,
    sessionId
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  const {
    requestAuth,
    setToast
  } = useContext(AuthContext);
  const {
    invalidateDrops
  } = useContext(ReactQueryWrapperContext);
  const {
    contextProfile,
    isLoaded: isSettingsLoaded,
    isEnabled: isQuickVoteEnabled,
    memesWaveId
  } = useMemesQuickVoteContext();
  let t2;
  if ($[0] !== contextProfile || $[1] !== memesWaveId || $[2] !== sessionId) {
    t2 = getOptimisticRemainingPowerKey({
      contextProfile,
      memesWaveId,
      sessionId
    });
    $[0] = contextProfile;
    $[1] = memesWaveId;
    $[2] = sessionId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const optimisticRemainingPowerKey = t2;
  let t3;
  if ($[4] !== optimisticRemainingPowerKey) {
    t3 = () => createInitialOptimisticRemainingPowerState(optimisticRemainingPowerKey);
    $[4] = optimisticRemainingPowerKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [optimisticRemainingPowerState, setOptimisticRemainingPowerState] = useState(t3);
  let t4;
  if ($[6] !== contextProfile || $[7] !== memesWaveId) {
    t4 = {
      contextProfile,
      memesWaveId
    };
    $[6] = contextProfile;
    $[7] = memesWaveId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    recentAmountsByRecency,
    setAndPersistRecentAmounts,
    setAndPersistSkippedDropIds,
    skippedDropIds
  } = useMemesQuickVoteStorage(t4);
  let t5;
  if ($[9] !== enabled) {
    t5 = {
      enabled
    };
    $[9] = enabled;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const summary = useMemesQuickVoteSummary(t5);
  let t6;
  if ($[11] !== enabled || $[12] !== sessionId || $[13] !== skippedDropIds) {
    t6 = {
      enabled,
      sessionId,
      skippedDropIds
    };
    $[11] = enabled;
    $[12] = sessionId;
    $[13] = skippedDropIds;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const discovery = useMemesQuickVoteDiscovery(t6);
  const {
    activeCandidateId,
    deferDropId,
    discoveredDropsById,
    hasPageFetchError,
    isExhausted: isDiscoveryExhausted,
    isFetchingPage,
    nextCandidateId,
    queue: discoveredQueue,
    removeDropId,
    resyncDiscovery,
    retryDiscovery,
    serverCount
  } = discovery;
  const {
    count: summaryCount,
    isPending: isSummaryPending,
    isQuickVoteEnabled: isSummaryQuickVoteEnabled,
    isSuccess: isSummarySuccess,
    refetch: refetchSummary,
    stats: summaryStats
  } = summary;
  let t7;
  if ($[15] !== optimisticRemainingPowerKey || $[16] !== optimisticRemainingPowerState) {
    t7 = getCurrentOptimisticRemainingPowerState({
      key: optimisticRemainingPowerKey,
      state: optimisticRemainingPowerState
    });
    $[15] = optimisticRemainingPowerKey;
    $[16] = optimisticRemainingPowerState;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const currentOptimisticRemainingPowerState = t7;
  const hasPendingOptimisticVotes = currentOptimisticRemainingPowerState.pendingVotes.length > 0;
  let t8;
  bb0: {
    const pendingSpent = currentOptimisticRemainingPowerState.pendingVotes.reduce(_temp, 0);
    const settledRemainingPower = currentOptimisticRemainingPowerState.settledRemainingPower;
    if (settledRemainingPower === null) {
      t8 = null;
      break bb0;
    }
    t8 = Math.max(0, settledRemainingPower - pendingSpent);
  }
  const optimisticRemainingPower = t8;
  let t9;
  if ($[18] !== currentOptimisticRemainingPowerState.optimisticVoteCount || $[19] !== currentOptimisticRemainingPowerState.summaryBaselineCount || $[20] !== hasPendingOptimisticVotes || $[21] !== isSummarySuccess || $[22] !== summaryCount) {
    t9 = deriveSummaryOptimismState({
      hasPendingVotes: hasPendingOptimisticVotes,
      isSummarySuccess,
      optimisticVoteCount: currentOptimisticRemainingPowerState.optimisticVoteCount,
      summaryBaselineCount: currentOptimisticRemainingPowerState.summaryBaselineCount,
      summaryCount
    });
    $[18] = currentOptimisticRemainingPowerState.optimisticVoteCount;
    $[19] = currentOptimisticRemainingPowerState.summaryBaselineCount;
    $[20] = hasPendingOptimisticVotes;
    $[21] = isSummarySuccess;
    $[22] = summaryCount;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const summaryOptimismState = t9;
  let t10;
  if ($[24] !== refetchSummary || $[25] !== removeDropId || $[26] !== setAndPersistSkippedDropIds) {
    t10 = dropId => {
      removeDropId(dropId);
      setAndPersistSkippedDropIds(current => current.filter(value => value !== dropId));
      refetchSummary();
    };
    $[24] = refetchSummary;
    $[25] = removeDropId;
    $[26] = setAndPersistSkippedDropIds;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  const handleInvalidatedDrop = t10;
  const t11 = enabled && isQuickVoteEnabled;
  let t12;
  if ($[28] !== activeCandidateId || $[29] !== discoveredDropsById || $[30] !== handleInvalidatedDrop || $[31] !== nextCandidateId || $[32] !== t11) {
    t12 = {
      activeCandidateId,
      discoveredDropsById,
      enabled: t11,
      nextCandidateId,
      onInvalidatedDrop: handleInvalidatedDrop
    };
    $[28] = activeCandidateId;
    $[29] = discoveredDropsById;
    $[30] = handleInvalidatedDrop;
    $[31] = nextCandidateId;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  const active = useMemesQuickVoteActiveDrop(t12);
  const effectiveOptimisticRemainingPower = active.hasFreshData && !hasPendingOptimisticVotes ? null : optimisticRemainingPower;
  let t13;
  if ($[34] !== isSummarySuccess || $[35] !== optimisticRemainingPowerKey || $[36] !== removeDropId || $[37] !== summaryCount) {
    t13 = (drop, amount) => {
      removeDropId(drop.id);
      setOptimisticRemainingPowerState(current_0 => {
        const baseState = getCurrentOptimisticRemainingPowerState({
          key: optimisticRemainingPowerKey,
          state: current_0
        });
        const reflectedVoteCount = baseState.summaryBaselineCount === null || !isSummarySuccess ? 0 : Math.max(0, baseState.summaryBaselineCount - summaryCount);
        const isSummaryCycleSettled = baseState.summaryBaselineCount !== null && baseState.optimisticVoteCount > 0 && baseState.pendingVotes.length === 0 && isSummarySuccess && reflectedVoteCount >= baseState.optimisticVoteCount;
        const shouldResetSummaryCycle = baseState.summaryBaselineCount === null || baseState.optimisticVoteCount === 0 || isSummaryCycleSettled;
        return {
          ...baseState,
          pendingVotes: [...baseState.pendingVotes, {
            amount,
            dropId: drop.id
          }],
          settledRemainingPower: baseState.pendingVotes.length === 0 ? drop.context_profile_context?.max_rating ?? null : baseState.settledRemainingPower ?? drop.context_profile_context?.max_rating ?? null,
          optimisticVoteCount: shouldResetSummaryCycle || !isSummarySuccess ? 1 : baseState.optimisticVoteCount + 1,
          summaryBaselineCount: isSummarySuccess && shouldResetSummaryCycle ? summaryCount : baseState.summaryBaselineCount
        };
      });
    };
    $[34] = isSummarySuccess;
    $[35] = optimisticRemainingPowerKey;
    $[36] = removeDropId;
    $[37] = summaryCount;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const handleVoteQueued = t13;
  let t14;
  if ($[39] !== optimisticRemainingPowerKey) {
    t14 = (_drop, _amount, nextRemainingPower) => {
      setOptimisticRemainingPowerState(current_1 => {
        const baseState_0 = getCurrentOptimisticRemainingPowerState({
          key: optimisticRemainingPowerKey,
          state: current_1
        });
        const currentVote = baseState_0.pendingVotes[0];
        if (!currentVote) {
          return {
            ...baseState_0,
            settledRemainingPower: nextRemainingPower
          };
        }
        return {
          ...baseState_0,
          pendingVotes: baseState_0.pendingVotes.slice(1),
          settledRemainingPower: nextRemainingPower
        };
      });
    };
    $[39] = optimisticRemainingPowerKey;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const handleVoteSuccess = t14;
  let t15;
  if ($[41] !== optimisticRemainingPowerKey || $[42] !== refetchSummary || $[43] !== resyncDiscovery) {
    t15 = (_drop_0, _amount_0) => {
      setOptimisticRemainingPowerState(current_2 => {
        const baseState_1 = getCurrentOptimisticRemainingPowerState({
          key: optimisticRemainingPowerKey,
          state: current_2
        });
        if (baseState_1.pendingVotes.length === 0) {
          return baseState_1;
        }
        const nextPendingVotes = baseState_1.pendingVotes.slice(1);
        const nextOptimisticVoteCount = Math.max(0, baseState_1.optimisticVoteCount - 1);
        return {
          ...baseState_1,
          pendingVotes: nextPendingVotes,
          optimisticVoteCount: nextOptimisticVoteCount,
          settledRemainingPower: nextPendingVotes.length === 0 ? null : baseState_1.settledRemainingPower,
          summaryBaselineCount: nextOptimisticVoteCount === 0 ? null : baseState_1.summaryBaselineCount
        };
      });
      resyncDiscovery();
      refetchSummary();
    };
    $[41] = optimisticRemainingPowerKey;
    $[42] = refetchSummary;
    $[43] = resyncDiscovery;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  const handleVoteFailure = t15;
  let t16;
  if ($[45] !== handleVoteFailure || $[46] !== handleVoteQueued || $[47] !== handleVoteSuccess || $[48] !== invalidateDrops || $[49] !== requestAuth || $[50] !== setAndPersistRecentAmounts || $[51] !== setAndPersistSkippedDropIds || $[52] !== setToast) {
    t16 = {
      requestAuth,
      setToast,
      invalidateDrops,
      onVoteFailure: handleVoteFailure,
      onVoteQueued: handleVoteQueued,
      onVoteSuccess: handleVoteSuccess,
      setAndPersistRecentAmounts,
      setAndPersistSkippedDropIds
    };
    $[45] = handleVoteFailure;
    $[46] = handleVoteQueued;
    $[47] = handleVoteSuccess;
    $[48] = invalidateDrops;
    $[49] = requestAuth;
    $[50] = setAndPersistRecentAmounts;
    $[51] = setAndPersistSkippedDropIds;
    $[52] = setToast;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  const {
    isVoting,
    submitVote
  } = useMemesQuickVoteSubmit(t16);
  let t17;
  if ($[54] !== deferDropId || $[55] !== setAndPersistSkippedDropIds) {
    t17 = drop_0 => {
      deferDropId(drop_0.id);
      setAndPersistSkippedDropIds(current_3 => appendSkippedDropId(current_3, drop_0.id));
    };
    $[54] = deferDropId;
    $[55] = setAndPersistSkippedDropIds;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  const skipDrop = t17;
  let t18;
  if ($[57] !== active.activeDrop || $[58] !== active.isLoading || $[59] !== discoveredQueue || $[60] !== effectiveOptimisticRemainingPower || $[61] !== enabled || $[62] !== hasPageFetchError || $[63] !== isDiscoveryExhausted || $[64] !== isFetchingPage || $[65] !== isSettingsLoaded || $[66] !== isSummaryPending || $[67] !== isSummaryQuickVoteEnabled || $[68] !== isSummarySuccess || $[69] !== recentAmountsByRecency || $[70] !== serverCount || $[71] !== summaryCount || $[72] !== summaryOptimismState.unreflectedVoteCount || $[73] !== summaryStats.votingLabel) {
    t18 = {
      activeDropCandidate: active.activeDrop,
      activeIsLoading: active.isLoading,
      discoveredQueue,
      effectiveOptimisticRemainingPower,
      enabled,
      hasPageFetchError,
      isDiscoveryExhausted,
      isFetchingPage,
      isSettingsLoaded,
      isSummaryPending,
      isSummaryQuickVoteEnabled,
      isSummarySuccess,
      recentAmountsByRecency,
      serverCount,
      summaryCount,
      unreflectedVoteCount: summaryOptimismState.unreflectedVoteCount,
      summaryVotingLabel: summaryStats.votingLabel
    };
    $[57] = active.activeDrop;
    $[58] = active.isLoading;
    $[59] = discoveredQueue;
    $[60] = effectiveOptimisticRemainingPower;
    $[61] = enabled;
    $[62] = hasPageFetchError;
    $[63] = isDiscoveryExhausted;
    $[64] = isFetchingPage;
    $[65] = isSettingsLoaded;
    $[66] = isSummaryPending;
    $[67] = isSummaryQuickVoteEnabled;
    $[68] = isSummarySuccess;
    $[69] = recentAmountsByRecency;
    $[70] = serverCount;
    $[71] = summaryCount;
    $[72] = summaryOptimismState.unreflectedVoteCount;
    $[73] = summaryStats.votingLabel;
    $[74] = t18;
  } else {
    t18 = $[74];
  }
  const derivedState = useDerivedMemesQuickVoteQueueState(t18);
  let t19;
  if ($[75] !== derivedState || $[76] !== isVoting || $[77] !== retryDiscovery || $[78] !== skipDrop || $[79] !== submitVote) {
    t19 = {
      ...derivedState,
      isVoting,
      retryDiscovery,
      submitVote,
      skipDrop
    };
    $[75] = derivedState;
    $[76] = isVoting;
    $[77] = retryDiscovery;
    $[78] = skipDrop;
    $[79] = submitVote;
    $[80] = t19;
  } else {
    t19 = $[80];
  }
  return t19;
};
function _temp(sum, vote) {
  return sum + vote.amount;
}